import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDesk } from "./context";
import { connection } from "./connection";
import { createEventInvalidator, invalidateContainers } from "./cache";
import { outbox } from "./outbox";
import { backoffMs } from "./outbox-core";

/**
 * Live updates over SSE (`/v1/events?token=`). Each change event invalidates the affected queries
 * (debounced). Reconnects with exponential backoff; a reconnect also refetches everything and
 * flushes the outbox, since events may have been missed while disconnected.
 */
const STALE_MS = 70_000;

export function useLiveEvents() {
  const { session } = useDesk();
  const qc = useQueryClient();
  useEffect(() => {
    let es: EventSource | null = null;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let closed = false;
    let everOpened = false;
    const invalidate = createEventInvalidator(qc);
    // The server sends a heartbeat every 25 s. A connection that stays silent much longer (sleep,
    // network change, half-open socket) is dead even if EventSource never reports an error.
    let lastSeen = Date.now();
    const seen = () => void (lastSeen = Date.now());
    const watchdog = setInterval(() => {
      if (es && es.readyState === EventSource.OPEN && Date.now() - lastSeen > STALE_MS) es.onerror?.(new Event("error"));
    }, 15_000);

    const onMessage = (ev: MessageEvent) => {
      seen();
      let entity: string | undefined;
      try {
        const data = JSON.parse(ev.data as string) as { entity?: string; events?: { entity?: string }[] };
        entity = data.entity ?? data.events?.[0]?.entity;
        data.events?.slice(1).forEach((e) => invalidate(e.entity));
      } catch {
        entity = undefined;
      }
      invalidate(entity);
    };

    const connect = () => {
      if (closed) return;
      connection.set({ live: "connecting" });
      const url = `${session.apiUrl}/v1/events?token=${encodeURIComponent(session.token)}`;
      es = new EventSource(url);
      es.onopen = () => {
        seen();
        attempts = 0;
        connection.set({ live: "live", unreachable: false });
        if (everOpened) void invalidateContainers(qc);
        everOpened = true;
        outbox.retryAll();
      };
      es.onmessage = onMessage;
      es.addEventListener("heartbeat", seen);
      ["change", "task", "plan", "area", "space", "project", "settings"].forEach((name) =>
        es!.addEventListener(name, onMessage as EventListener),
      );
      es.onerror = () => {
        es?.close();
        es = null;
        connection.set({ live: "offline" });
        attempts += 1;
        timer = setTimeout(connect, backoffMs(attempts, Math.random()));
      };
    };

    const onOnline = () => {
      if (timer) clearTimeout(timer);
      if (!es) connect();
      outbox.retryAll();
    };
    window.addEventListener("online", onOnline);
    connect();
    return () => {
      closed = true;
      clearInterval(watchdog);
      window.removeEventListener("online", onOnline);
      if (timer) clearTimeout(timer);
      es?.close();
    };
  }, [session.apiUrl, session.token, qc]);
}
